import type {
  SalesOrder,
  SalesOrderItem,
  SaleFulfillmentDetail,
  SaleFulfillmentGroup,
  SaleFulfillmentGroupStatus,
} from '../../types/sales.types';
import { getGroupStatusPresentation, resolveLogisticsStage } from './saleFulfillmentPresentation';

export type ItemPhysicalPresence = 'IN_TRANSIT' | 'AT_BRANCH' | 'UNKNOWN';

export interface OrderDeliveryContext {
  order: SalesOrder;
  /** Mixed-sale logistics detail; optional for local-only orders. */
  fulfillmentDetail?: SaleFulfillmentDetail | null;
}

/** Map sales-order-item id → fulfillment group. */
export function buildItemFulfillmentGroupMap(
  groups: SaleFulfillmentGroup[] | null | undefined,
): Map<number, SaleFulfillmentGroup> {
  const map = new Map<number, SaleFulfillmentGroup>();
  for (const group of groups ?? []) {
    for (const item of group.items ?? []) {
      map.set(item.id, group);
    }
  }
  return map;
}

/** Map sales-order-item id → fulfillment group status (effective / stored). */
export function buildItemFulfillmentGroupStatusMap(
  groups: SaleFulfillmentGroup[] | null | undefined,
  pieceAtSellingBranchByItemId?: Map<number, boolean>,
): Map<number, string> {
  const map = new Map<number, string>();
  for (const group of groups ?? []) {
    for (const item of group.items ?? []) {
      const effective = resolveLogisticsStage({
        storedStatus: group.status,
        requestStatus: group.inventoryRequest?.status,
        transferStatus: group.inventoryTransfer?.status,
        pieceAtSellingBranch: pieceAtSellingBranchByItemId?.get(item.id),
      });
      map.set(item.id, effective);
    }
  }
  return map;
}

/**
 * Whether a remote piece has physically arrived at the selling branch.
 * Prefer logistics signals (group RECEIVED / order COMPLETED) over WIP
 * location alone — location can be missing/stale on some payloads.
 */
export function isRemoteItemArrivedAtSellingBranch(
  item: SalesOrderItem,
  ctx: OrderDeliveryContext,
): boolean {
  const { order, fulfillmentDetail } = ctx;
  const fulfillmentStatus = String(
    fulfillmentDetail?.fulfillmentStatus ?? order.fulfillmentStatus ?? '',
  );

  if (fulfillmentStatus === 'COMPLETED' || fulfillmentStatus === 'READY') {
    return true;
  }

  const groupStatusByItemId = buildItemFulfillmentGroupStatusMap(fulfillmentDetail?.groups);
  const groupStatus = groupStatusByItemId.get(item.id);
  if (groupStatus === 'RECEIVED') {
    return true;
  }

  const wip = item.warehouseInventoryProduct;
  const orderLocationId = order.location?.id;
  if (wip?.location?.id != null && orderLocationId != null) {
    if (wip.location.id === orderLocationId && wip.status !== 'IN_TRANSIT') {
      const availability = String(wip.availabilityStatus ?? '');
      if (availability !== 'IN_REQUEST' && availability !== 'IN_TRANSIT') {
        return true;
      }
    }
  }

  return false;
}

/**
 * Logistics stage for a line item — same SSOT as the fulfillment timeline
 * (SOL/TRL facts + WIP arrival at selling branch).
 */
export function getItemLogisticsStage(
  item: SalesOrderItem,
  ctx: OrderDeliveryContext,
): SaleFulfillmentGroupStatus | null {
  if (item.fulfillmentType !== 'REMOTE') {
    return null;
  }

  const pieceAtSellingBranch = isRemoteItemArrivedAtSellingBranch(item, ctx);
  const group = buildItemFulfillmentGroupMap(ctx.fulfillmentDetail?.groups).get(item.id);
  if (group) {
    return resolveLogisticsStage({
      storedStatus: group.status,
      requestStatus: group.inventoryRequest?.status,
      transferStatus: group.inventoryTransfer?.status,
      pieceAtSellingBranch,
    });
  }

  if (pieceAtSellingBranch) {
    return 'RECEIVED';
  }

  const wip = item.warehouseInventoryProduct;
  if (wip?.status === 'IN_TRANSIT') {
    return 'IN_TRANSIT';
  }
  if (String(wip?.availabilityStatus ?? '') === 'IN_REQUEST') {
    return 'PENDING_SOL';
  }

  // Remote, not yet arrived, no detail/WIP signal: treat as pending solicitud
  // (not optimistic "en tránsito") until logistics detail loads.
  return 'PENDING_SOL';
}

export function getItemPhysicalPresence(
  item: SalesOrderItem,
  ctx: OrderDeliveryContext,
): ItemPhysicalPresence {
  if (item.fulfillmentType !== 'REMOTE') {
    return 'AT_BRANCH';
  }
  const stage = getItemLogisticsStage(item, ctx);
  if (stage === 'RECEIVED') {
    return 'AT_BRANCH';
  }
  if (isRemoteItemArrivedAtSellingBranch(item, ctx)) {
    return 'AT_BRANCH';
  }
  return 'IN_TRANSIT';
}

/** Piece is physically at selling branch and can be handed to the customer. */
export function isOrderItemDeliverable(
  item: SalesOrderItem,
  order: SalesOrder,
  fulfillmentDetail?: SaleFulfillmentDetail | null,
): boolean {
  // Return/cancel voids customer handoff for the whole order.
  if (order.deliveryStatus === 'CANCELLED') {
    return false;
  }

  if (item.isDelivered) return false;

  if (item.fulfillmentType !== 'REMOTE') {
    return true;
  }

  return isRemoteItemArrivedAtSellingBranch(item, { order, fulfillmentDetail });
}

export function getDeliverableUndeliveredItems(
  order: SalesOrder,
  fulfillmentDetail?: SaleFulfillmentDetail | null,
): SalesOrderItem[] {
  return (order.items ?? []).filter((item) =>
    isOrderItemDeliverable(item, order, fulfillmentDetail),
  );
}

export function getItemDisplayName(item: SalesOrderItem): string {
  return (
    item.warehouseInventoryProduct?.product?.name ||
    item.warehouseInventoryProduct?.productCodeUnique ||
    `Pieza #${item.id}`
  );
}

/**
 * Origin branch shown in the pieces table: source for remotes,
 * selling branch for locals (never the abstract label "Local").
 */
export function getItemOriginLocationName(item: SalesOrderItem, order: SalesOrder): string {
  if (item.fulfillmentType === 'REMOTE') {
    return item.sourceLocation?.name ?? '—';
  }
  return order.location?.name ?? '—';
}

/**
 * Location/logistics chip for the pieces table. Uses the same stage SSOT as
 * "Solicitudes y logística" (En solicitud | En tránsito | En sucursal).
 */
export function getItemPresenceChipState(
  item: SalesOrderItem,
  ctx: OrderDeliveryContext,
): { label: string; color: 'success' | 'info' | 'warning' | 'error' | 'default' } {
  if (item.fulfillmentType !== 'REMOTE') {
    return { label: 'En sucursal', color: 'success' };
  }

  const stage = getItemLogisticsStage(item, ctx);
  if (!stage) {
    return { label: '—', color: 'default' };
  }

  const presentation = getGroupStatusPresentation(stage);
  const colorByStatusType: Record<string, 'success' | 'info' | 'warning' | 'error' | 'default'> = {
    success: 'success',
    info: 'info',
    warning: 'warning',
    error: 'error',
    default: 'default',
  };

  return {
    label: presentation.label,
    color: colorByStatusType[presentation.statusType] ?? 'default',
  };
}

/** Piece-level customer handoff chip: Entregada | No entregada */
export function getItemDeliveryChipState(
  item: SalesOrderItem,
  _ctx?: OrderDeliveryContext,
): { label: string; color: 'success' | 'default' } {
  if (item.isDelivered) {
    return { label: 'Entregada', color: 'success' };
  }
  return { label: 'No entregada', color: 'default' };
}
